import { useMemo, useState } from 'react';
import { useIsMobile } from '../lib/useIsMobile';
import type { Contact, ContactInfo, ContactSpec, GalContact } from '@zimbra-webif/sdk';
import {
  useAddressBooks,
  useContacts,
  useCreateContact,
  useGalSearch,
} from '../lib/contacts-queries';
import { IconSearch, IconShare } from './icons';
import './contacts.css';

/* ------------------------------------------------------------------ */
/* Field helpers                                                       */
/* ------------------------------------------------------------------ */

/**
 * Fold a raw `<cn>` (ContactInfo, e.g. a GAL entry) `a` array into a flat
 * attribute dict. Local contacts already arrive folded as `Contact._attrs`
 * from the SDK, but GAL entries are raw ContactInfo, so we normalise here.
 */
function foldAttrs(ci: ContactInfo): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of ci.a ?? []) {
    if (typeof a.n === 'string' && typeof a._content === 'string') {
      out[a.n] = a._content;
    }
  }
  return out;
}

/** A unified card model from either a local Contact or a GAL ContactInfo. */
interface ContactCard {
  /** stable key for selection (contact id, or synthesized for GAL hits) */
  key: string;
  /** the folded field dict */
  attrs: Record<string, string>;
  /** whether this came from the GAL */
  fromGal: boolean;
}

function localToCard(c: Contact): ContactCard {
  return { key: c.id ?? `local-${c.fileAsStr ?? ''}`, attrs: c._attrs, fromGal: false };
}

function galToCard(ci: GalContact, idx: number): ContactCard {
  const attrs = foldAttrs(ci);
  const key = ci.id ?? ci.ref ?? attrs.email ?? `gal-${idx}`;
  return { key: `gal:${key}`, attrs, fromGal: true };
}

function displayName(attrs: Record<string, string>): string {
  const full = (attrs.fullName ?? '').trim();
  if (full) return full;
  const fn = (attrs.firstName ?? '').trim();
  const ln = (attrs.lastName ?? '').trim();
  const joined = `${fn} ${ln}`.trim();
  if (joined) return joined;
  return attrs.nickname ?? attrs.email ?? attrs.company ?? '(no name)';
}

function primaryEmail(attrs: Record<string, string>): string {
  return attrs.email ?? attrs.email2 ?? attrs.email3 ?? '';
}

function initials(name: string): string {
  const parts = name
    .replace(/[<>"]/g, '')
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Ordered, labelled detail fields to render in the right-hand card. */
const DETAIL_FIELDS: Array<{ key: string; label: string; kind?: 'email' | 'tel' }> = [
  { key: 'jobTitle', label: 'Title' },
  { key: 'department', label: 'Department' },
  { key: 'company', label: 'Company' },
  { key: 'email', label: 'Email', kind: 'email' },
  { key: 'email2', label: 'Email 2', kind: 'email' },
  { key: 'email3', label: 'Email 3', kind: 'email' },
  { key: 'mobilePhone', label: 'Mobile', kind: 'tel' },
  { key: 'workPhone', label: 'Work', kind: 'tel' },
  { key: 'homePhone', label: 'Home', kind: 'tel' },
  { key: 'workFax', label: 'Fax', kind: 'tel' },
  { key: 'workStreet', label: 'Street' },
  { key: 'workCity', label: 'City' },
  { key: 'workState', label: 'State' },
  { key: 'workPostalCode', label: 'Postal Code' },
  { key: 'workCountry', label: 'Country' },
  { key: 'notes', label: 'Notes' },
];

/* ------------------------------------------------------------------ */
/* Create form                                                         */
/* ------------------------------------------------------------------ */

function CreateContactForm({
  folderId,
  onDone,
}: {
  folderId: string;
  onDone: () => void;
}) {
  const create = useCreateContact();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mobilePhone, setMobile] = useState('');
  const [company, setCompany] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const attrs = [
      { n: 'firstName', _content: firstName },
      { n: 'lastName', _content: lastName },
      { n: 'email', _content: email },
      { n: 'mobilePhone', _content: mobilePhone },
      { n: 'company', _content: company },
    ].filter((a) => a._content.trim() !== '');
    if (attrs.length === 0) return;
    const spec: ContactSpec = { folderId, attrs };
    create.mutate(spec, { onSuccess: onDone });
  }

  return (
    <form className="c-form" onSubmit={submit}>
      <h2>New contact</h2>
      <div className="c-form-row">
        <label>
          First name
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
        </label>
        <label>
          Last name
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </label>
      </div>
      <label>
        Email
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Mobile
        <input value={mobilePhone} onChange={(e) => setMobile(e.target.value)} />
      </label>
      <label>
        Company
        <input value={company} onChange={(e) => setCompany(e.target.value)} />
      </label>
      <div className="c-err">{create.isError ? 'Could not save contact.' : ''}</div>
      <div className="c-form-actions">
        <button type="button" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Detail card                                                         */
/* ------------------------------------------------------------------ */

function DetailCard({ card }: { card: ContactCard }) {
  const { attrs } = card;
  const name = displayName(attrs);
  const sub = attrs.jobTitle && attrs.company ? `${attrs.jobTitle} · ${attrs.company}` : attrs.jobTitle ?? attrs.company ?? '';
  const rows = DETAIL_FIELDS.map((f) => ({ ...f, value: attrs[f.key] })).filter(
    (f) => typeof f.value === 'string' && f.value.trim() !== '',
  );

  return (
    <div className="c-detail">
      <div className="c-detail-hero">
        <div className="c-photo">{initials(name)}</div>
        <div className="c-detail-name">{name}</div>
        {sub ? <div className="c-detail-sub">{sub}</div> : null}
        {card.fromGal ? <div className="c-detail-sub">Directory (GAL)</div> : null}
      </div>
      <div className="c-fields">
        {rows.length === 0 ? (
          <div className="c-detail-sub">No additional details.</div>
        ) : (
          rows.map((f) => (
            <div className="c-field" key={f.key}>
              <span className="c-label">{f.label}</span>
              <span className="c-value">
                {f.kind === 'email' ? (
                  <a href={`mailto:${f.value}`}>{f.value}</a>
                ) : f.kind === 'tel' ? (
                  <a href={`tel:${f.value}`}>{f.value}</a>
                ) : (
                  f.value
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Screen                                                              */
/* ------------------------------------------------------------------ */

export function ContactsScreen() {
  const [activeBook, setActiveBook] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const booksQ = useAddressBooks();
  const books = booksQ.data ?? [];

  // Default to the first personal address book once books load.
  const effectiveBook = activeBook ?? books.find((b) => !b.shared)?.id ?? books[0]?.id ?? null;

  const contactsQ = useContacts(effectiveBook);
  const galQ = useGalSearch(search);

  const localCards = useMemo(
    () => (contactsQ.data ?? []).map(localToCard),
    [contactsQ.data],
  );
  const galCards = useMemo(
    () => (galQ.data ?? []).map(galToCard),
    [galQ.data],
  );

  const term = search.trim().toLowerCase();

  // Visible list: local contacts filtered by the term, plus GAL hits when searching.
  const visible = useMemo(() => {
    const filteredLocal = term
      ? localCards.filter((c) => {
          const hay = `${displayName(c.attrs)} ${primaryEmail(c.attrs)} ${c.attrs.company ?? ''}`.toLowerCase();
          return hay.includes(term);
        })
      : localCards;
    // De-dupe GAL hits that share an email with a visible local contact.
    const localEmails = new Set(
      filteredLocal.map((c) => primaryEmail(c.attrs).toLowerCase()).filter(Boolean),
    );
    const galExtra = galCards.filter((g) => {
      const e = primaryEmail(g.attrs).toLowerCase();
      return e === '' || !localEmails.has(e);
    });
    return [...filteredLocal, ...galExtra];
  }, [localCards, galCards, term]);

  const selected = useMemo(
    () => visible.find((c) => c.key === selectedKey) ?? null,
    [visible, selectedKey],
  );

  const personalBooks = books.filter((b) => !b.shared);
  const sharedBooks = books.filter((b) => b.shared);

  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  function selectBook(id: string) {
    setActiveBook(id);
    setSelectedKey(null);
    setCreating(false);
    setDrawerOpen(false);
  }

  return (
    <div
      className={`contacts-shell${isMobile ? ' m-shell' : ''}${drawerOpen ? ' m-drawer' : ''}${
        isMobile && (selected || creating) ? ' m-detail' : ''
      }`}
    >
      {isMobile && drawerOpen ? (
        <div className="m-backdrop" onClick={() => setDrawerOpen(false)} />
      ) : null}
      <aside className="pane sidebar m-side">
        <div className="pane-hdr">Address Books</div>
        <div className="pane-scroll">
          {booksQ.isLoading ? (
            <div className="loading">Loading…</div>
          ) : (
            <>
              {personalBooks.map((b) => (
                <div
                  key={b.id}
                  className={`ab${b.id === effectiveBook ? ' active' : ''}`}
                  style={{ paddingLeft: 10 + b.depth * 14 }}
                  onClick={() => selectBook(b.id)}
                >
                  <span className="ab-name">{b.name}</span>
                  {b.count ? <span className="ab-count">{b.count}</span> : null}
                </div>
              ))}
              {sharedBooks.length > 0 ? <div className="ab-section">Shared</div> : null}
              {sharedBooks.map((b) => (
                <div
                  key={b.id}
                  className={`ab${b.id === effectiveBook ? ' active' : ''}`}
                  style={{ paddingLeft: 10 + b.depth * 14 }}
                  onClick={() => selectBook(b.id)}
                  title={b.owner ? `Shared by ${b.owner}` : undefined}
                >
                  <span className="ab-name">
                    <IconShare className="ab-glyph" />
                    {b.name}
                  </span>
                  {b.count ? <span className="ab-count">{b.count}</span> : null}
                </div>
              ))}
            </>
          )}
        </div>
      </aside>

      <section className="pane list m-list-pane">
        <div className="pane-hdr c-listhdr">
          <button className="m-burger" aria-label="Address books" onClick={() => setDrawerOpen(true)}>
            ☰
          </button>
          <label className="c-search">
            <IconSearch />
            <input
              type="search"
              placeholder="Search contacts & directory…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="c-listcount">
            <span>
              {contactsQ.isFetching || galQ.isFetching
                ? 'Searching…'
                : `${visible.length} contact${visible.length === 1 ? '' : 's'}`}
            </span>
            {effectiveBook ? (
              <button
                className="c-newbtn"
                onClick={() => {
                  setCreating(true);
                  setSelectedKey(null);
                }}
              >
                + New
              </button>
            ) : null}
          </div>
        </div>
        <div className="pane-scroll">
          {contactsQ.isLoading ? (
            <div className="loading">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="loading">No contacts</div>
          ) : (
            visible.map((c) => {
              const name = displayName(c.attrs);
              const email = primaryEmail(c.attrs);
              return (
                <div
                  key={c.key}
                  className={`c-row${selectedKey === c.key ? ' active' : ''}`}
                  onClick={() => {
                    setSelectedKey(c.key);
                    setCreating(false);
                  }}
                >
                  <div className="avatar">{initials(name)}</div>
                  <div className="c-body">
                    <div className="c-name">{name}</div>
                    <div className="c-email">{email || '(no email)'}</div>
                  </div>
                  {c.fromGal ? <span className="c-gal-badge">GAL</span> : null}
                </div>
              );
            })
          )}
        </div>
      </section>

      <main className="pane reading m-detail-pane">
        <button
          className="m-back"
          aria-label="Back"
          onClick={() => {
            setSelectedKey(null);
            setCreating(false);
          }}
        >
          ‹ Back
        </button>
        {creating && effectiveBook ? (
          <CreateContactForm folderId={effectiveBook} onDone={() => setCreating(false)} />
        ) : selected ? (
          <DetailCard card={selected} />
        ) : (
          <div className="empty">Select a contact</div>
        )}
      </main>
    </div>
  );
}
